import {
  buildParticipantIntakeFormMeta,
  buildSurveyFilename,
  encodeSurveyFormId,
  getPendingQuestionsIdsForSurvey,
  hasPendingQuestions,
} from '../utils/surveyFunctions';
import * as libCon from '../community-hats-js-library/Constants';
import * as locCon from '../LocalConstants';
import CustomText from '../elements/CustomText';
import Header from '../components/Header';
import {
  GenericMultipleSurveysFormStructureButton,
  GenericMultipleSurveysQuestions,
  GenericMultipleSurveysProgressComponentAndExport,
} from '../elements/SurveyElements';
import { getATOField, getNestedStoredValue, RefStoredMultipleValues, setStoredValue } from '../hooks/StoreHooks';
import { RefContextLanguage } from '../providers/LanguageProvider';

export const PARTICIPANT_INTAKE_FORM_CONFIG = {
  encoder: encodeSurveyFormId(libCon.PARTICIPANT_INTAKE_FORM_PREFIX),
  configuration: libCon.LATEST_PARTICIPANT_INTAKE_FORM,
  finalConfigurationKey: locCon.PARTICIPANT_INTAKE_FORM_FINAL_CONFIGURATION,
  folder: libCon.PARTICIPANT_INTAKE_FORM_FOLDER,
  metaBuilder: buildParticipantIntakeFormMeta,
  buildFileName: buildSurveyFilename,
  getSurveySerial: () => getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_SEWA_ID),
  processConfiguration: async () => {
    // Checks for pending questions
    // All are stored under the general Participant Intake Form
    let missingQuestions = getNestedStoredValue(locCon.PENDING_FORM_QUESTIONS_OBJECT, libCon.PARTICIPANT_INTAKE_FORM);

    if (!hasPendingQuestions(missingQuestions)) {
      setStoredValue(locCon.PARTICIPANT_INTAKE_FORM_FINAL_CONFIGURATION, libCon.LATEST_PARTICIPANT_INTAKE_FORM);
      return;
    }

    //Filers by the Form Type
    let missingQuestionsIds = getPendingQuestionsIdsForSurvey(missingQuestions, libCon.PARTICIPANT_INTAKE_FORM_NAME);

    let formConfig = structuredClone(libCon.LATEST_PARTICIPANT_INTAKE_FORM);
    formConfig[libCon.SURVEY_STRUCTURE] = formConfig[libCon.SURVEY_STRUCTURE].filter((ob) =>
      missingQuestionsIds.includes(ob[libCon.SURVEY_QUESTION_ID])
    );
    setStoredValue(locCon.PARTICIPANT_INTAKE_FORM_FINAL_CONFIGURATION, formConfig);
  },
};

export const PARTICIPANT_STREET_VENDOR_INTAKE_FORM_CONFIG = {
  encoder: encodeSurveyFormId(libCon.PARTICIPANT_STREET_VENDOR_INTAKE_FORM_PREFIX),
  configuration: libCon.LATEST_PARTICIPANT_STREET_VENDOR_INTAKE_FORM,
  finalConfigurationKey: locCon.PARTICIPANT_STREET_VENDOR_INTAKE_FORM_FINAL_CONFIGURATION,
  folder: libCon.PARTICIPANT_STREET_VENDOR_INTAKE_FORM_FOLDER,
  metaBuilder: buildParticipantIntakeFormMeta,
  buildFileName: buildSurveyFilename,
  getSurveySerial: () => getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_SEWA_ID),
  processConfiguration: async () => {
    // Checks for pending questions
    // All are stored under the general Participant Intake Form
    let missingQuestions = getNestedStoredValue(locCon.PENDING_FORM_QUESTIONS_OBJECT, libCon.PARTICIPANT_INTAKE_FORM);

    if (!hasPendingQuestions(missingQuestions)) {
      if (getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_TRADE) !== libCon.ATF_STREET_VENDOR) {
        let formConfig = structuredClone(libCon.LATEST_PARTICIPANT_STREET_VENDOR_INTAKE_FORM);
        formConfig[libCon.SURVEY_STRUCTURE] = [];
        setStoredValue(locCon.PARTICIPANT_STREET_VENDOR_INTAKE_FORM_FINAL_CONFIGURATION, formConfig);
      } else
        setStoredValue(
          locCon.PARTICIPANT_STREET_VENDOR_INTAKE_FORM_FINAL_CONFIGURATION,
          libCon.LATEST_PARTICIPANT_STREET_VENDOR_INTAKE_FORM
        );
      return;
    }

    //Filers by the Form Type
    // Might not be street vendor but forces questions
    let missingQuestionsIds = getPendingQuestionsIdsForSurvey(
      missingQuestions,
      libCon.PARTICIPANT_STREET_VENDOR_INTAKE_FORM_NAME
    );

    let formConfig = structuredClone(libCon.LATEST_PARTICIPANT_STREET_VENDOR_INTAKE_FORM);
    formConfig[libCon.SURVEY_STRUCTURE] = formConfig[libCon.SURVEY_STRUCTURE].filter((ob) =>
      missingQuestionsIds.includes(ob[libCon.SURVEY_QUESTION_ID])
    );
    setStoredValue(locCon.PARTICIPANT_STREET_VENDOR_INTAKE_FORM_FINAL_CONFIGURATION, formConfig);
  },
};

export function ParticipantIntakeForm() {
  const i18n = RefContextLanguage();

  // TOGGLE INTAKE FORMS
  const includedConfigurations = [PARTICIPANT_INTAKE_FORM_CONFIG, PARTICIPANT_STREET_VENDOR_INTAKE_FORM_CONFIG];

  const configurationStatusId = locCon.PARTICIPANT_INTAKE_FORM_CONFIGURATION_STATUS;
  const instructionsText = i18n.t('participantIntakeFormInstructions');
  const sendingStatusId = locCon.PIF_CONNECTION_SENDING_ANSWERS_STATUS;

  const encodersArray = includedConfigurations.map((config) => config.encoder);
  const surveyConfigurationsArray = includedConfigurations.map((config) => config.configuration);
  const folderNamesArray = includedConfigurations.map((config) => config.folder);
  const metaBuildersArray = includedConfigurations.map((config) => config.metaBuilder);
  const buildFilenamesArray = includedConfigurations.map((config) => config.buildFileName);
  const getSurveySerialsArray = includedConfigurations.map((config) => config.getSurveySerial);
  const processConfigurationArray = includedConfigurations.map((config) => config.processConfiguration);

  const finalConfigurationsObject = RefStoredMultipleValues(
    includedConfigurations.map((config) => config.finalConfigurationKey)
  );

  return (
    <div className="mainAppContainer">
      <div className="pageContainer">
        <Header long={true} includeBackButton={true} />

        <div className="verticalSection" style={{ marginBottom: 25 }}>
          <CustomText type={locCon.SECTION_TITLE_TEXT} style={{ marginBottom: '1.5vh' }}>
            {i18n.t('participantIntakeForm')}
          </CustomText>

          <GenericMultipleSurveysFormStructureButton
            surveyConfigurationsArray={surveyConfigurationsArray}
            configurationStatusId={configurationStatusId}
            sendingStatusId={sendingStatusId}
            processConfigurationArray={processConfigurationArray}
          />

          <GenericMultipleSurveysQuestions
            encodersArray={encodersArray}
            surveyConfigurationsArray={Object.values(finalConfigurationsObject)}
            configurationStatusId={configurationStatusId}
            instructionsText={instructionsText}
          />

          <GenericMultipleSurveysProgressComponentAndExport
            surveyConfigurationsArray={Object.values(finalConfigurationsObject)}
            sendingStatusId={sendingStatusId}
            configurationStatusId={configurationStatusId}
            buildFilenamesArray={buildFilenamesArray}
            folderNamesArray={folderNamesArray}
            encodersArray={encodersArray}
            metaBuildersArray={metaBuildersArray}
            getSurveySerialsArray={getSurveySerialsArray}
          />
        </div>
      </div>
    </div>
  );
}

export default ParticipantIntakeForm;
